import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/public/ui/Icon';
import { pickerTriggerClass } from '@/lib/formStyles';

interface TimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  hasError?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

function parseTime(s: string): { h: number; m: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(s ?? '');
  if (!match) return null;
  const h = +match[1];
  const m = +match[2];
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export default function TimePicker({
  id,
  value,
  onChange,
  disabled,
  placeholder = 'Selecionar hora',
  hasError,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hourColRef = useRef<HTMLDivElement>(null);
  const minColRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const parsed = parseTime(value);
    const targetH = parsed?.h ?? 12;
    const targetM = parsed?.m ?? 0;
    const centerWithinColumn = (container: HTMLDivElement | null, item: HTMLElement | null) => {
      if (!container || !item) return;
      container.scrollTop = item.offsetTop - container.clientHeight / 2 + item.offsetHeight / 2;
    };
    const animId = requestAnimationFrame(() => {
      centerWithinColumn(
        hourColRef.current,
        hourColRef.current?.querySelector<HTMLElement>(`[data-hour="${targetH}"]`) ?? null,
      );
      centerWithinColumn(
        minColRef.current,
        minColRef.current?.querySelector<HTMLElement>(`[data-minute="${targetM}"]`) ?? null,
      );
    });
    return () => cancelAnimationFrame(animId);
  }, [open, value]);

  const parsed = parseTime(value);
  const display = parsed ? `${pad(parsed.h)}:${pad(parsed.m)}` : '';

  const setHour = (h: number) => {
    const m = parsed?.m ?? 0;
    onChange(`${pad(h)}:${pad(m)}`);
  };
  const setMinute = (m: number) => {
    const h = parsed?.h ?? 0;
    onChange(`${pad(h)}:${pad(m)}`);
  };
  const clear = () => {
    onChange('');
    setOpen(false);
  };

  const triggerClasses = pickerTriggerClass({ open, hasError, disabled });

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={triggerClasses}
      >
        <span className={display ? '' : 'text-on-surface-light/60'}>{display || placeholder}</span>
        <Icon name="schedule" size="sm" className="text-on-surface-light flex-shrink-0" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Selecionar hora"
          className="surface-card-elevated absolute left-0 z-50 mt-2 w-60 max-w-[calc(100vw-2rem)]"
        >
          <div className="flex items-center justify-between px-3 pt-3">
            <span className="font-headline text-on-surface-light/55 text-[0.7rem] font-bold tracking-wider uppercase">
              Hora
            </span>
            <span className="font-headline text-primary text-base font-bold tabular-nums">
              {display || '--:--'}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2 p-3">
            <div>
              <div className="text-on-surface-light/55 py-1 text-center text-[0.65rem] font-bold tracking-wider uppercase">
                Hora
              </div>
              <div
                ref={hourColRef}
                className="border-line bg-background max-h-56 [scrollbar-width:none] overflow-y-auto rounded-md border p-1 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                {HOURS.map((h) => {
                  const selected = parsed?.h === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      data-hour={h}
                      onClick={() => setHour(h)}
                      aria-pressed={selected}
                      aria-label={`Hora ${pad(h)}`}
                      className={[
                        'w-full rounded-lg py-2 text-sm tabular-nums transition-colors duration-150',
                        selected
                          ? 'bg-primary font-bold text-white'
                          : 'text-on-surface hover:bg-primary/10 font-medium',
                      ].join(' ')}
                    >
                      {pad(h)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-on-surface-light/55 py-1 text-center text-[0.65rem] font-bold tracking-wider uppercase">
                Minuto
              </div>
              <div
                ref={minColRef}
                className="border-line bg-background max-h-56 [scrollbar-width:none] overflow-y-auto rounded-md border p-1 [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                {MINUTES.map((m) => {
                  const selected = parsed?.m === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      data-minute={m}
                      onClick={() => setMinute(m)}
                      aria-pressed={selected}
                      aria-label={`Minuto ${pad(m)}`}
                      className={[
                        'w-full rounded-lg py-2 text-sm tabular-nums transition-colors duration-150',
                        selected
                          ? 'bg-primary font-bold text-white'
                          : 'text-on-surface hover:bg-primary/10 font-medium',
                      ].join(' ')}
                    >
                      {pad(m)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-line flex items-center justify-between border-t px-3 py-2">
            <button
              type="button"
              onClick={clear}
              disabled={!value}
              className="text-on-surface-light hover:bg-primary/5 hover:text-primary rounded-md px-2 py-1 text-xs font-semibold transition-colors disabled:opacity-30"
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-primary hover:bg-primary/10 rounded-md px-2 py-1 text-xs font-semibold transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
